import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Preloader from '../../components/common/Preloader';
import SEO from '../../components/common/SEO';
import superAdminApiClient from '../../api/superAdminApiClient';
import { toast } from 'react-toastify';
import './SuperAdminDashboard.css';

const Dashboard = () => {
    const [stats, setStats] = useState({
        totalPaidVendors: 0,
        totalTrialVendors: 0,
        totalFreeVendors: 0,
        totalSubmittedLeads: 0,
        totalHolidays: 0,
        totalHotels: 0,
        totalCabs: 0,
        totalYatras: 0,
        totalDoctors: 0,
        totalHospitals: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await superAdminApiClient.get('/superadmin/dashboard-stats');
            if (response.data.success) {
                setStats(response.data.stats);
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching stats:", error);
            toast.error("Failed to fetch dashboard statistics");
            setLoading(false);
        }
    };

    const statCards = [
        {
            title: "Total Paid Vendors",
            count: stats.totalPaidVendors,
            icon: "ri-money-rupee-circle-fill",
            colorClass: "text-success",
            bgIcon: "bg-success-subtle",
            gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            link: "/superadmin/vendors/paid"
        },
        {
            title: "Trial Vendors",
            count: stats.totalTrialVendors,
            icon: "ri-timer-flash-fill",
            colorClass: "text-warning",
            bgIcon: "bg-warning-subtle",
            gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            link: "/superadmin/vendors/trial"
        },
        {
            title: "Free Vendors",
            count: stats.totalFreeVendors,
            icon: "ri-user-smile-fill",
            colorClass: "text-info",
            bgIcon: "bg-info-subtle",
            gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
            link: "/superadmin/vendors/free"
        },
        {
            title: "Total Submitted Leads",
            count: stats.totalSubmittedLeads,
            icon: "ri-file-list-3-fill",
            colorClass: "text-primary",
            bgIcon: "bg-primary-subtle",
            gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
            link: "/superadmin/leads"
        }
    ];

    const listingCards = [
        {
            title: "Holidays Listed",
            count: stats.totalHolidays || 0,
            icon: "ri-flight-takeoff-line",
            colorClass: "text-danger",
            bgIcon: "bg-danger-subtle",
            gradient: "linear-gradient(135deg, #ff6b6b 0%, #ee5253 100%)",
            link: "/superadmin/listings-approval/holidays",
            badgeText: "Holidays"
        },
        {
            title: "Hotels Listed",
            count: stats.totalHotels || 0,
            icon: "ri-hotel-line",
            colorClass: "text-primary",
            bgIcon: "bg-primary-subtle",
            gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
            link: "/superadmin/listings-approval/hotels",
            badgeText: "Hotels"
        },
        {
            title: "Cabs Listed",
            count: stats.totalCabs || 0,
            icon: "ri-taxi-line",
            colorClass: "text-warning",
            bgIcon: "bg-warning-subtle",
            gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            link: "/superadmin/listings-approval/cabs",
            badgeText: "Cabs"
        },
        {
            title: "Yatras Listed",
            count: stats.totalYatras || 0,
            icon: "ri-compass-3-line",
            colorClass: "text-purple",
            bgIcon: "bg-purple-subtle",
            gradient: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
            link: "/superadmin/listings-approval/yatras",
            badgeText: "Yatras"
        },
        {
            title: "Medical Tourism Doctors",
            count: stats.totalDoctors || 0,
            icon: "ri-user-heart-line",
            colorClass: "text-teal",
            bgIcon: "bg-teal-subtle",
            gradient: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
            link: "/superadmin/listings-approval/doctors",
            badgeText: "Doctors"
        },
        {
            title: "Medical Tourism Hospitals",
            count: stats.totalHospitals || 0,
            icon: "ri-hospital-line",
            colorClass: "text-success",
            bgIcon: "bg-success-subtle",
            gradient: "linear-gradient(135deg, #10b981 0%, #047857 100%)",
            link: "/superadmin/listings-approval/hospitals",
            badgeText: "Hospitals"
        }
    ];

    if (loading) {
        return <Preloader />;
    }

    return (
        <React.Fragment>
            <SEO
                title="Dashboard | Mangoloops Super Admin"
                description="Manage vendors, leads, and platform settings from the Mangoloops Super Admin Dashboard."
            />

            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0 fw-bold">Super Admin Dashboard</h4>
                    </div>
                </div>
            </div>

            <div className="row">
                {statCards.map((card, index) => (
                    <div className="col-xl-3 col-md-6 mb-4" key={index}>
                        <div className="card trending-card h-100">
                            <div className="decor-blob"></div>
                            <div className="card-body p-4" style={{ zIndex: 1, position: 'relative' }}>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div className="trending-icon-box" style={{ background: card.gradient }}>
                                        <i className={card.icon}></i>
                                    </div>
                                    <div className="text-end">
                                        <p className="text-uppercase trending-title mb-1">
                                            {card.title}
                                        </p>
                                        <h4 className="trending-count mb-0">
                                            {loading ? <span className="spinner-border spinner-border-sm text-muted"></span> : card.count}
                                        </h4>
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-top border-light d-flex align-items-center justify-content-between">
                                    <Link to={card.link} className={`trending-link ${card.colorClass}`}>
                                        View Details <i className="ri-arrow-right-line align-middle ms-1"></i>
                                    </Link>
                                    <div className="badge bg-light text-dark rounded-pill px-2 py-1 shadow-sm border border-light">
                                        <i className="ri-pulse-line text-success me-1"></i> Live
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row">
                {listingCards.map((card, index) => (
                    <div className="col-xl-4 col-md-6 mb-4" key={index}>
                        <div className="card trending-card h-100">
                            <div className="decor-blob"></div>
                            <div className="card-body p-4" style={{ zIndex: 1, position: 'relative' }}>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div className="trending-icon-box" style={{ background: card.gradient }}>
                                        <i className={card.icon}></i>
                                    </div>
                                    <div className="text-end">
                                        <p className="text-uppercase trending-title mb-1">
                                            {card.title}
                                        </p>
                                        <h4 className="trending-count mb-0">
                                            {loading ? <span className="spinner-border spinner-border-sm text-muted"></span> : card.count}
                                        </h4>
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-top border-light d-flex align-items-center justify-content-between">
                                    <Link to={card.link} className={`trending-link ${card.colorClass}`}>
                                        Approval Page <i className="ri-arrow-right-line align-middle ms-1"></i>
                                    </Link>
                                    <div className="badge bg-light text-dark rounded-pill px-2 py-1 shadow-sm border border-light">
                                        {card.badgeText}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

        </React.Fragment>
    );
};

export default Dashboard;
